import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { Badge } from "@/components/Badge/Badge";
import { Column } from "@/components/Datatable/types";
import {
  LibraryModuleListItem,
  LibraryModuleStatus,
} from "@/modules/learningCenter/features/ModuleLibrary/types/libraryModule";

interface CreateLibraryModuleColumnsProps {
  onEditModule: (uuid: string) => void;
  onPublishModule: (module: LibraryModuleListItem) => void;
  onArchiveModule: (module: LibraryModuleListItem) => void;
}

const statusVariant = (
  status: LibraryModuleStatus,
): "default" | "grey" | "destructive" => {
  switch (status) {
    case "published":
      return "default";
    case "archived":
      return "destructive";
    case "draft":
    default:
      return "grey";
  }
};

function LibraryModuleRowActions({
  module,
  onEditModule,
  onPublishModule,
  onArchiveModule,
}: {
  module: LibraryModuleListItem;
} & CreateLibraryModuleColumnsProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="h-8 w-8 p-0 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors"
          variant="ghost"
        >
          <span className="sr-only">Open menu</span>
          <EllipsisVerticalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[160px] p-2 bg-white dark:bg-slate-800"
      >
        <DropdownMenuItem
          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          onClick={() => onEditModule(module.uuid)}
        >
          Edit
        </DropdownMenuItem>
        {module.status === "draft" && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            onClick={() => onPublishModule(module)}
          >
            Publish
          </DropdownMenuItem>
        )}
        {module.status !== "archived" && (
          <DropdownMenuItem
            className="flex items-center px-3 py-2 text-sm cursor-pointer text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
            onClick={() => onArchiveModule(module)}
          >
            Archive
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function createLibraryModuleColumns({
  onEditModule,
  onPublishModule,
  onArchiveModule,
}: CreateLibraryModuleColumnsProps): Column<LibraryModuleListItem>[] {
  return [
    {
      header: "Title",
      accessorKey: "title",
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="text-gray-900 dark:text-white">
            {row.original.title}
          </span>
          {row.original.description && (
            <span className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
              {row.original.description}
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Status",
      accessorKey: "status",
      enableSorting: true,
      cell: ({ row }) => (
        <Badge variant={statusVariant(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      header: "Difficulty",
      accessorKey: "difficulty",
      cell: ({ row }) =>
        row.original.difficulty ? (
          <Badge variant="grey">{row.original.difficulty}</Badge>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      header: "Duration",
      accessorKey: "estimatedMinutes",
      cell: ({ row }) =>
        row.original.estimatedMinutes != null ? (
          <span className="text-gray-900 dark:text-white">
            {row.original.estimatedMinutes} min
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      header: "Tags",
      accessorKey: "tags",
      cell: ({ row }) =>
        row.original.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.original.tags.map((tag) => (
              <Badge key={tag} variant="grey">
                {tag}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      header: "Updated",
      accessorKey: "updatedAt",
      enableSorting: true,
      cell: ({ row }) =>
        row.original.updatedAt ? (
          <span className="text-gray-900 dark:text-white">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-gray-500 dark:text-gray-400">—</span>
        ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <LibraryModuleRowActions
          module={row.original}
          onArchiveModule={onArchiveModule}
          onEditModule={onEditModule}
          onPublishModule={onPublishModule}
        />
      ),
    },
  ];
}
